import React, { useState, useRef } from "react";
import { Move, Trash2 } from "lucide-react";
import type { Meeting } from "../hooks/useMeetingHistory";
import type { Folder } from "../services/folderService";

interface HistoryMeetingCardProps {
  meeting: Meeting;
  folders: Folder[];
  isEn: boolean;
  loadingMeetingId: string | number | null;
  movingMeetingId?: string | number | null;
  showFolderInfo?: boolean;
  onLoad: (meeting: Meeting) => void;
  onMoveMeeting: (meetingId: string | number, folderId: string | number | null) => void;
  onUpdateTags?: (meetingId: string | number, tags: string[]) => void;
  onDeleteMeeting?: (meetingId: string | number) => void;
  confirmDeleteId?: string | number | null;
  setConfirmDeleteId?: (id: string | number | null) => void;
  deletingId?: string | number | null;
  formatDate: (isoString: string) => string;
  formatDuration: (meeting: Meeting) => string;
}

export function HistoryMeetingCard({
  meeting,
  folders,
  isEn,
  loadingMeetingId,
  movingMeetingId,
  showFolderInfo,
  onLoad,
  onMoveMeeting,
  onUpdateTags,
  onDeleteMeeting,
  confirmDeleteId,
  setConfirmDeleteId,
  deletingId,
  formatDate,
  formatDuration,
}: HistoryMeetingCardProps) {
  const [moving, setMoving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const [editingTags, setEditingTags] = useState(false);
  const [editTagsInput, setEditTagsInput] = useState(meeting.tags ? meeting.tags.join(", ") : "");

  // Safe folder matching (comparing as strings to avoid number vs string mismatch)
  const assignedFolder =
    meeting.folder_id !== null && meeting.folder_id !== undefined
      ? folders.find((f) => String(f._id) === String(meeting.folder_id))
      : null;
  const folderName = assignedFolder ? assignedFolder.name : isEn ? "Unassigned" : "Sin asignar";

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(meeting._id));
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ meetingId: meeting._id, sourceFolder: meeting.folder_id ?? "root" })
    );

    const preview = document.createElement("div");
    preview.style.position = "absolute";
    preview.style.top = "-9999px";
    preview.style.left = "-9999px";
    preview.style.padding = "8px 12px";
    preview.style.background = "rgba(15, 23, 42, 0.95)";
    preview.style.color = "white";
    preview.style.border = "1px solid rgba(148, 163, 184, 0.35)";
    preview.style.borderRadius = "12px";
    preview.style.fontSize = "12px";
    preview.style.fontFamily = "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    preview.style.boxShadow = "0 10px 30px rgba(15, 23, 42, 0.3)";
    preview.style.maxWidth = "220px";
    preview.style.whiteSpace = "nowrap";
    preview.style.overflow = "hidden";
    preview.style.textOverflow = "ellipsis";
    preview.textContent = `Moviendo: ${meeting.title || (isEn ? "Untitled" : "Sin título")}`;

    document.body.appendChild(preview);
    dragPreviewRef.current = preview;
    e.dataTransfer.setDragImage(preview, 12, 12);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
      dragPreviewRef.current = null;
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onLoad(meeting)}
      className={`group py-2 px-3 bg-[var(--bg3)] border border-[var(--b1)] hover:border-[var(--b2)] rounded-[8px] cursor-pointer transition-all duration-200 flex flex-col gap-1 relative ${
        isDragging ? "opacity-90 ring-2 ring-accent/60" : ""
      } ${movingMeetingId != null && String(movingMeetingId) !== String(meeting._id) ? "opacity-80" : ""}`}
    >
      {(String(loadingMeetingId) === String(meeting._id) || String(movingMeetingId) === String(meeting._id)) && (
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px] flex items-center justify-center rounded-[8px] z-10">
          <div className="w-4 h-4 border-2 border-accent/20 border-t-accent rounded-full animate-spin" />
        </div>
      )}

      {/* Top Row: Date, Duration, Badge, Move Button & Delete */}
      <div className="flex justify-between items-center gap-1.5">
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-dim)] tabular-nums truncate">
          <span>{formatDate(meeting.created_at)}</span>
          <span className="opacity-40">•</span>
          <span className="whitespace-nowrap">{formatDuration(meeting)}</span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {/* Folder move inside card */}
          {moving ? (
            <select
              autoFocus
              disabled={movingMeetingId === meeting._id}
              onBlur={() => setMoving(false)}
              onChange={(e) => {
                const val = e.target.value;
                const targetFolder = folders.find((f) => String(f._id) === val);
                const targetId = val === "root" ? null : targetFolder ? targetFolder._id : val;
                onMoveMeeting(meeting._id, targetId);
                setMoving(false);
              }}
              className="bg-[var(--bg4)] text-xs border border-[var(--b1)] rounded px-1 py-0.5 outline-none text-[var(--text)] cursor-pointer"
              defaultValue={
                meeting.folder_id !== null && meeting.folder_id !== undefined
                  ? String(meeting.folder_id)
                  : "root"
              }
            >
              <option value="root">{isEn ? "Root" : "Raíz"}</option>
              {folders.map((f) => (
                <option key={f._id} value={String(f._id)}>
                  {f.name}
                </option>
              ))}
            </select>
          ) : movingMeetingId === meeting._id ? (
            <div className="opacity-100 p-0.5 rounded bg-[var(--surface)] flex items-center justify-center">
              <div className="w-3 h-3 border-2 border-[var(--text-dim)]/30 border-t-[var(--text-dim)] rounded-full animate-spin" />
            </div>
          ) : String(movingMeetingId) === String(meeting._id) ? (
            <div className="opacity-100 p-0.5 rounded bg-[var(--surface)] flex items-center justify-center">
              <div className="w-3 h-3 border-2 border-[var(--text-dim)]/30 border-t-[var(--text-dim)] rounded-full animate-spin" />
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMoving(true);
              }}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 text-[var(--text-dim)] hover:text-[var(--text)] cursor-pointer transition-opacity"
              title={isEn ? "Move to folder" : "Mover de carpeta"}
            >
              <Move size={10} />
            </button>
          )}

          {/* Delete Button */}
          {onDeleteMeeting && setConfirmDeleteId && (
            confirmDeleteId === meeting._id ? (
              <div className="flex items-center gap-0.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMeeting(meeting._id);
                  }}
                  disabled={deletingId === meeting._id}
                  className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-1.5 py-0.5 rounded transition-all cursor-pointer"
                >
                  {isEn ? "Del" : "Borrar"}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(null);
                  }}
                  className="text-xs font-medium text-[var(--text-dim)] hover:text-[var(--text)] bg-[var(--bg2)] px-1 py-0.5 rounded border border-[var(--b1)] cursor-pointer"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteId(meeting._id);
                }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 text-[var(--text-dim)] hover:text-red-400 cursor-pointer transition-opacity"
                title={isEn ? "Delete meeting" : "Eliminar reunión"}
              >
                <Trash2 size={10} />
              </button>
            )
          )}

          {meeting.mode === "minuta" ? (
            <span className="text-xs font-bold tracking-[0.04em] px-1 py-0.5 rounded-full whitespace-nowrap bg-[var(--badge-m-bg)] text-[var(--badge-m-text)]">
              M
            </span>
          ) : (
            <span className="text-xs font-bold tracking-[0.04em] px-1 py-0.5 rounded-full whitespace-nowrap bg-[var(--badge-tm-bg)] text-[var(--badge-tm-text)]">
              T+M
            </span>
          )}
        </div>
      </div>

      {/* Title & Click to load summary */}
      <div className="text-xs font-medium text-[var(--text)] leading-snug line-clamp-2 hover:text-accent transition-colors">
        {meeting.title || (isEn ? "Untitled Meeting" : "Reunión sin título")}
      </div>

      {/* Bottom: Tags and optional Folder Info */}
      <div className="flex justify-between items-center text-xs text-[var(--text-dim)] pt-0.5">
        {/* Tags Editor / Viewer */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {editingTags ? (
            <input
              type="text"
              autoFocus
              value={editTagsInput}
              onChange={(e) => setEditTagsInput(e.target.value)}
              onBlur={() => {
                const parsedTags = editTagsInput
                  .split(",")
                  .map((t: string) => t.trim())
                  .filter((t: string) => t.length > 0);
                const currentTags = meeting.tags || [];
                const isSame =
                  parsedTags.length === currentTags.length &&
                  parsedTags.every((t: string, i: number) => t === currentTags[i]);
                if (!isSame && onUpdateTags) {
                  onUpdateTags(meeting._id, parsedTags);
                }
                setEditingTags(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  setEditingTags(false);
                }
              }}
              placeholder={isEn ? "tag1, tag2..." : "etiqueta1, etiqueta2..."}
              className="bg-[var(--bg4)] border border-[var(--b1)] rounded px-1.5 py-0.5 text-xs outline-none text-[var(--text)] w-[100px]"
            />
          ) : (
            <div
              className="flex items-center gap-1 group/tags cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setEditTagsInput(meeting.tags ? meeting.tags.join(", ") : "");
                setEditingTags(true);
              }}
              title={isEn ? "Edit tags" : "Editar etiquetas"}
            >
              {meeting.tags && meeting.tags.length > 0 ? (
                <div className="flex gap-1 overflow-hidden max-w-[120px]">
                  {meeting.tags.slice(0, 2).map((t: string, idx: number) => (
                    <span
                      key={idx}
                      className="bg-accent/10 border border-accent/20 text-[var(--accent)] text-xs font-mono px-1 rounded-full"
                    >
                      {t}
                    </span>
                  ))}
                  {meeting.tags.length > 2 && <span className="text-[7.5px]">+{meeting.tags.length - 2}</span>}
                </div>
              ) : (
                <span className="text-xs italic text-[var(--text-muted)] opacity-60 group-hover/tags:opacity-100 transition-opacity">
                  {isEn ? "+ Add tag" : "+ Etiqueta"}
                </span>
              )}
              <svg
                className="w-2.5 h-2.5 text-[var(--text-muted)] opacity-60 group-hover/tags:opacity-100 transition-opacity"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </div>
          )}
        </div>

        {showFolderInfo && (
          <div
            className="flex items-center gap-1 text-xs text-[var(--text-muted)] bg-[var(--bg2)] px-1.5 py-0.5 rounded border border-[var(--b1)] flex-shrink-0 ml-2 max-w-[100px] overflow-hidden whitespace-nowrap text-ellipsis"
            title={folderName}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="9"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-1.2-1.8A2 2 0 0 0 8.55 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
            </svg>
            <span className="truncate">{folderName}</span>
          </div>
        )}
      </div>
    </div>
  );
}
